import React, { useState, useEffect } from 'react';
import { Property, AppUser } from '../types';
import { XMarkIcon, SpinnerIcon, CheckCircleIcon, EnvelopeIcon, DevicePhoneMobileIcon, IdentificationIcon } from './Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../constants';
import { submitLead, LeadData } from '../services/leadService';

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property;
  appUser: AppUser | null;
}

type ModalStatus = 'form' | 'loading' | 'success' | 'error';

const idTypes = [
  { value: 'CC', label: 'Cédula de ciudadanía' },
  { value: 'CE', label: 'Cédula de extranjería' },
  { value: 'ITIN', label: 'ITIN number' },
  { value: 'LC', label: 'Licencia de conducción' },
  { value: 'NIT', label: 'Nit' },
  { value: 'PAS', label: 'Pasaporte' },
  { value: 'PEP', label: 'Permiso especial de permanencia' },
  { value: 'RC', label: 'Registro Civil' },
  { value: 'SSN', label: 'Social Security Number (SSN)' },
  { value: 'TI', label: 'Tarjeta de identidad' },
  { value: 'TE', label: 'Tarjeta extranjería' },
  { value: 'TDE', label: 'Tipo de documento extranjero' },
];

export const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({ isOpen, onClose, property, appUser }) => {
    const [status, setStatus] = useState<ModalStatus>('form');
    const [formData, setFormData] = useState<LeadData>({
        nombre: '',
        correo: '',
        movil: '',
        tipoId: 'CC',
        numeroId: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            setStatus('form');
            setErrors({});
            setFormData({
                nombre: '',
                correo: '',
                movil: '',
                tipoId: 'CC',
                numeroId: '',
            });
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    
    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido.';
        if (!formData.correo.trim() || !/\S+@\S+\.\S+/.test(formData.correo.trim())) {
            newErrors.correo = 'El correo electrónico es requerido y debe ser válido.';
        }
        if (!formData.movil.trim()) {
            newErrors.movil = 'El teléfono es requerido.';
        } else if (!/^\d+$/.test(formData.movil.trim())) {
            newErrors.movil = 'El teléfono solo debe contener números.';
        }
        if (!formData.tipoId) newErrors.tipoId = 'El tipo de identificación es requerido.';
        if (!formData.numeroId.trim()) newErrors.numeroId = 'El número de identificación es requerido.';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setStatus('loading');
        
        const result = await submitLead(property, formData, appUser);

        if (result.success) {
            setStatus('success');
        } else {
            setErrors({ form: result.message });
            setStatus('error');
        }
    };

    const renderInput = (name: keyof LeadData, label: string, type: string, icon?: React.ReactNode, extraProps = {}) => (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
            <div className="relative mt-1">
                {icon && <div className="pointer-events-none w-5 h-5 absolute top-1/2 transform -translate-y-1/2 left-3 text-gray-400">{icon}</div>}
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={formData[name as keyof LeadData]}
                    onChange={handleChange}
                    required
                    className={`w-full ${icon ? 'pl-10' : 'px-3'} py-2 border rounded-md shadow-sm focus:outline-none focus:ring-sky-500 sm:text-sm ${errors[name] ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-sky-500'}`}
                    {...extraProps}
                />
            </div>
            {errors[name] && <p className="mt-1 text-xs text-red-600">{errors[name]}</p>}
        </div>
    );

    const renderContent = () => {
         switch(status) {
            case 'loading': 
                return <div className="text-center p-12"><SpinnerIcon className="w-12 h-12 text-sky-600 mx-auto" /><p className="mt-4">Enviando tus datos...</p></div>;
            case 'success': 
                return (
                    <div className="text-center p-12">
                        <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto" />
                        <h3 className="text-xl mt-4 font-semibold">¡Gracias por tu interés!</h3>
                        <p className="mt-2 text-gray-600">Hemos recibido tus datos. Pronto un asesor se pondrá en contacto contigo.</p>
                        <button onClick={onClose} className="mt-6 w-full px-4 py-2 text-white rounded-md" style={{backgroundColor: CIEN_CUADRAS_BUTTON_BLUE}}>Cerrar</button>
                    </div>
                );
            case 'error':
            case 'form':
            default:
                return (
                    <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[inherit]">
                        <div className="p-6 space-y-4">
                            {errors.form && <div className="p-3 bg-red-100 text-red-800 rounded-md text-sm">{errors.form}</div>}
                            
                            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Déjanos tus datos de contacto</h4>
                            <p className="text-sm text-gray-600">Un asesor se comunicará contigo para darte más detalles sobre el inmueble con código <span className="font-bold">{property.codigo}</span>.</p>
                            
                            <div className="grid grid-cols-1 gap-4">
                                {renderInput('nombre', 'Nombre Completo', 'text')}
                                {renderInput('correo', 'Correo Electrónico', 'email', <EnvelopeIcon/>)}
                                {renderInput('movil', 'Teléfono Celular', 'tel', <DevicePhoneMobileIcon/>, { pattern: "\\d*", placeholder: "3001234567" })}
                                <div>
                                    <label htmlFor="tipoId" className="block text-sm font-medium text-gray-700">Tipo y Número de Documento</label>
                                    <div className="mt-1 flex">
                                        <select name="tipoId" id="tipoId" value={formData.tipoId} onChange={handleChange} className={`appearance-none border border-r-0 border-gray-300 rounded-l-md focus:outline-none focus:ring-sky-500 focus:border-sky-500 text-sm pl-2 pr-8 ${errors.tipoId ? 'border-red-500' : ''}`}>
                                            {idTypes.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
                                        </select>
                                        <div className="relative flex-grow">
                                            <IdentificationIcon className="pointer-events-none w-5 h-5 absolute top-1/2 transform -translate-y-1/2 left-3 text-gray-400" />
                                            <input type="text" name="numeroId" value={formData.numeroId} onChange={handleChange} required className={`w-full pl-10 pr-3 py-2 border rounded-r-md shadow-sm focus:outline-none focus:ring-sky-500 sm:text-sm ${errors.numeroId ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-sky-500'}`}/>
                                        </div>
                                    </div>
                                    {errors.numeroId && <p className="mt-1 text-xs text-red-600">{errors.numeroId}</p>}
                                    {errors.tipoId && <p className="mt-1 text-xs text-red-600">{errors.tipoId}</p>}
                                </div>
                            </div>
                        </div>

                        <footer className="sticky bottom-0 bg-gray-50 -m-6 mt-6 px-6 py-4 border-t border-gray-200 flex justify-end items-center space-x-3">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">Cancelar</button>
                            <button type="submit" className="px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm flex items-center" style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}>
                                Enviar mis datos
                            </button>
                        </footer>
                    </form>
                );
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="lead-modal-title">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
                <header className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 z-10 flex justify-between items-center">
                    <h3 id="lead-modal-title" className="text-lg font-medium leading-6 text-gray-900">Estoy Interesado</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Cerrar modal"><XMarkIcon className="w-6 h-6" /></button>
                </header>
                {renderContent()}
            </div>
        </div>
    );
};